import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Query, QueryGetVisitorGroupsForTenantArgs } from 'graphql/generated/graphqlTypes';
import {
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_DOORS_FOR_TENANT,
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { TranslatedHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect, useMemo } from 'react';
import { getDoorsAssignedToVisitorGroups } from 'utils/Lock/Lock';
import { getLockGroupAssignedToVisitor } from 'utils/LockGroup/LockGroup';
import {
  filterOnlyCoContractorsGroups,
  filterVisitorGroupsWithMember,
  filterVisitorGroupsWithoutInternalGroups
} from 'utils/VisitorGroup/VisitorGroup';
import { rebuildVisitorGroupsFromIds } from 'v2/utils/rebuildVisitorGroupsFromIds';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
// Only ids are selected for the members and access grant locations (device groups) and doors (devices), so the
// groups are rebuilt with `members`, `locations` and `doors` for the helpers below.
const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  QueryGetVisitorGroupsForTenantArgs
> = GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS;
const GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getLocationsForTenant'>> =
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS;
const GET_DOORS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> = GET_DOORS_FOR_TENANT;

interface Props extends TranslatedHookProps {
  visitorId: string;
}
const useVisitorDetailsAccessOverview = ({ handleFetchError, translation, visitorId }: Props) => {
  const {
    loading: visitorGroupsLoading,
    data: visitorGroupsData,
    error: visitorGroupsError
  } = useQuery(GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED);

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  const {
    loading: locationsLoading,
    data: locationsData,
    error: locationsError
  } = useQuery(GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED);

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.device_group_error_fetching);
      console.error(locationsError);
    }
  }, [locationsError]);

  const { loading: doorsLoading, data: doorsData, error: doorsError } = useQuery(GET_DOORS_FOR_TENANT_TYPED);

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  // Cached data is shown straight away; only the very first load shows the loader.
  const loadingVisitorGroups = visitorGroupsLoading && !visitorGroupsData;
  const loadingLocations = locationsLoading && !locationsData;
  const loadingDoors = doorsLoading && !doorsData;

  // True when no query is on its first load and at least one with cached data is refreshing. `loading` is used instead
  // of the network status because a failed refetch leaves the status on `error`, which would keep the "refreshing"
  // indicator on until the next success.
  const isRefetching =
    !loadingVisitorGroups &&
    !loadingLocations &&
    !loadingDoors &&
    ((!!visitorGroupsData && visitorGroupsLoading) ||
      (!!locationsData && locationsLoading) ||
      (!!doorsData && doorsLoading));

  useRegisterBackgroundRefetch(isRefetching);

  const locations = useMemo(() => locationsData?.getLocationsForTenant ?? [], [locationsData]);

  const allVisitorGroups = useMemo(
    () =>
      visitorGroupsData
        ? rebuildVisitorGroupsFromIds(visitorGroupsData.getVisitorGroupsForTenant, locations)
        : undefined,
    [visitorGroupsData, locations]
  );

  const visitorDetailsVisitorGroupsInternal = filterOnlyCoContractorsGroups(allVisitorGroups);
  const visitorDetailsVisitorGroupsAllWithoutInternal = filterVisitorGroupsWithoutInternalGroups(allVisitorGroups);
  const visitorDetailsVisitorGroupsContainingVisitor = filterVisitorGroupsWithMember(allVisitorGroups, visitorId);

  return {
    visitorDetailsVisitorGroupsContainingVisitor,
    visitorDetailsVisitorGroupsAll: allVisitorGroups || [],
    visitorDetailsVisitorGroupsAllWithoutInternal,
    visitorDetailsVisitorGroupsInternal,
    loadingGetVisitorDetailsVisitorGroups: loadingVisitorGroups,
    visitorDetailsLockGroups: getLockGroupAssignedToVisitor(
      locations,
      visitorDetailsVisitorGroupsContainingVisitor || []
    ),
    loadingGetVisitorDetailsLockGroups: loadingLocations,
    visitorDetailsDoors: getDoorsAssignedToVisitorGroups(
      visitorDetailsVisitorGroupsContainingVisitor || [],
      doorsData?.getDoorsForTenant || []
    ),
    loadingGetVisitorDetailsDoors: loadingDoors
  };
};

export default useVisitorDetailsAccessOverview;
